import React, { useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import noop from 'lodash/noop';
import { asyncCreateIssueTypeModalEntryPoint } from '@atlassian/jira-create-issue-type-modal/entrypoint.tsx';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { RuntimePropsOfEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import type { ConfigureSource } from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import type { UFOExperience } from '@atlassian/ufo';

export type CreateIssueTypeModalProps = {
	onIssueTypeCreated?: () => void;
	onOpenManageTypes?: () => void;
	onOpen: () => void;
	onClose: () => void;
	projectKey: string;
	shouldFetchProjectData?: boolean;
	source: ConfigureSource;
	ufoExperience?: UFOExperience;
};
type RuntimeProps = RuntimePropsOfEntryPoint<typeof asyncCreateIssueTypeModalEntryPoint>;

export const CreateIssueTypeModal = forwardRef(
	(
		{
			onIssueTypeCreated = noop,
			onOpenManageTypes = noop,
			onOpen,
			onClose,
			source,
			shouldFetchProjectData = false,
			projectKey,
			ufoExperience,
		}: CreateIssueTypeModalProps,
		ref,
	) => {
		const cloudId = useCloudId();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
			asyncCreateIssueTypeModalEntryPoint,
			{
				projectKey,
				cloudId,
				shouldFetchProjectData,
			},
		);

		useImperativeHandle(ref, () => ({
			load: entryPointActions.load,
			preload: entryPointActions.preload,
		}));

		const onCloseCreateIssueTypeModal = useCallback(() => {
			fireUIAnalytics(
				createAnalyticsEvent({
					actionSubject: 'inlineCreateIssueTypeModal',
					action: 'closed',
				}),
			);
			onClose();
		}, [createAnalyticsEvent, onClose]);

		const entryPointProps: RuntimeProps = useMemo(
			() => ({
				onIssueTypeCreated,
				issueTypesCountLimit: 30,
				onClose: onCloseCreateIssueTypeModal,
				onOpen,
				onOpenManageTypes,
				source,
				ufoExperience,
			}),
			[
				onIssueTypeCreated,
				onCloseCreateIssueTypeModal,
				onOpen,
				onOpenManageTypes,
				source,
				ufoExperience,
			],
		);

		return (
			<ModalEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				entryPointActions={entryPointActions}
				runtimeProps={entryPointProps}
				id="create-issue-type-modal"
			/>
		);
	},
);
